import React, { ReactElement } from 'react';
import { IconButton, AppBar as NativeAppbar, Toolbar, Typography, styled } from '@mui/material';
import { SIDEBAR_WIDTH } from 'constants/constants';
import { ArrowBack, Menu } from '@mui/icons-material';
import { useApp } from 'hooks/app';

type AppbarProps = {
  title: string;
  ActionsComponent?: ReactElement;
  backAction?(): void;
  Tab?: ReactElement;
};

interface MakeStyleProps {
  ActionsComponent: boolean;
}

interface StyledAppBarProps {
  isopen: boolean;
}

const AppBarTabsSpace = styled('div')(props => ({
  marginBottom: 45,
  [props.theme.breakpoints.down('md')]: {
    marginBottom: 48,
  },
}));

const CustomActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const Actions = styled('div')({
  display: 'flex',
  justifyContent: 'end',
  gap: 7,
});

const StyledAppBar = styled(NativeAppbar, {
  shouldForwardProp: prop => prop !== 'isopen',
})<StyledAppBarProps>(({ theme, isopen }) => ({
  background: '#fff',
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  color: theme.palette.text.primary,
  zIndex: 1102,
  left: isopen ? SIDEBAR_WIDTH : 0,
  width: isopen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%',
  transition: 'left 300ms ease, width 300ms ease',
  '& .MuiIconButton-root': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    left: 0,
    width: '100%',
  },
  '@media print': {
    display: 'none',
  },
}));

interface TabsAppbarProps {
  isopen: boolean;
}

const TabsAppBar = styled(NativeAppbar)<TabsAppbarProps>(({ theme, isopen }) => ({
  top: 50,
  left: isopen ? SIDEBAR_WIDTH : 0,
  width: isopen ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%',
  background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.98) 0%, #ffffff 100%)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  color: theme.palette.text.primary,
  [theme.breakpoints.between('xs', 'xs') + ' and (orientation: landscape)']: {
    top: 48,
  },
  [theme.breakpoints.down('md')]: {
    left: 0,
    width: '100%',
  },
  transition: 'left 300ms ease, width 300ms ease',
}));

const CustomToolbar = styled(Toolbar, { shouldForwardProp: prop => prop !== 'ActionsComponent' })<MakeStyleProps>(
  props => ({
    minHeight: '50px!important',
    display: props.ActionsComponent ? 'grid' : 'flex',
    justifyContent: props.ActionsComponent ? '' : 'space-between',
    gridTemplateColumns: '1fr 1fr',
    position: 'relative',
    [props.theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
      display: 'flex',
    },
  }),
);

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: 0,
  '& .brand': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  '& .divider': {
    width: 1,
    height: 20,
    background: theme.palette.divider,
  },
  '& .page-title': {
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    fontSize: 12,
    '& .divider': {
      display: 'none',
    },
  },
}));

const Appbar: React.FC<AppbarProps> = ({ title, ActionsComponent, backAction, Tab }) => {
  const app = useApp();
  const isDesktopMenuOpen = app.isOpenedMenu && !app.isMobile && app.windowWidth > 960;

  return (
    <>
      <StyledAppBar position="fixed" isopen={isDesktopMenuOpen}>
        <CustomToolbar ActionsComponent={!!ActionsComponent}>
          <CustomActions>
            {backAction && app.isMobile && app.windowWidth < 960 ? (
              <IconButton onClick={backAction} color="inherit">
                <ArrowBack />
              </IconButton>
            ) : (
              <IconButton onClick={app.handleOpenMenu} color="inherit">
                <Menu />
              </IconButton>
            )}
            <Title>
              <Typography className="brand">Universo Plástica</Typography>
              <span className="divider" />
              <Typography className="page-title">{title}</Typography>
            </Title>
          </CustomActions>

          {ActionsComponent && <Actions>{ActionsComponent}</Actions>}
        </CustomToolbar>
      </StyledAppBar>

      {Tab && (
        <>
          <TabsAppBar isopen={isDesktopMenuOpen}>{Tab}</TabsAppBar>
          <AppBarTabsSpace />
        </>
      )}
    </>
  );
};

export default Appbar;
